import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { PIC, SVG_MUI } from "@ryde/assets";
import {
  BulkActionsPanel,
  IBulkActionItem,
  useGenericExportBulkAction,
} from "@ryde/controls";
import { LayoutRight, LayoutRightCrudContentStatic } from "@ryde/layout";
import {
  useBookingsBulkActions,
  useBookingsNavigation,
  useCrudEditBulk,
  useModalConfirm,
  usePageContext,
} from "@ryde/redux";
import {
  bulkCloneFormDrawerAF,
  bulkOperationsDrawerAF,
  ConfirmResult,
  DrawerType,
  selectedCellsBookingsSelector,
  selectedTableRowsIdsAtomFamily,
  useDrawers,
} from "@ryde/shared";

export const BulkOperationsBookingsDrawer: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const { confirmDialogResult } = useModalConfirm();
  const { isCalendarBookingView } = useBookingsNavigation();

  const selectedBookingsCalender = useRecoilValue(
    selectedCellsBookingsSelector,
  );
  const selectedGridBookingIds = useRecoilValue(
    selectedTableRowsIdsAtomFamily(entity),
  );

  const selectedBookingIds = useMemo(() => {
    return isCalendarBookingView
      ? selectedBookingsCalender.map(bookingCell => bookingCell.id)
      : selectedGridBookingIds;
  }, [isCalendarBookingView, selectedBookingsCalender, selectedGridBookingIds]);

  const setBulkOperationsDrawerOpened = useSetRecoilState(
    bulkOperationsDrawerAF(entity),
  );
  const setBulkCloneFormDrawerOpened = useSetRecoilState(
    bulkCloneFormDrawerAF(entity),
  );
  const { openBulkEditForm } = useCrudEditBulk(entity);
  const { openDrawer } = useDrawers();
  const { bulkArchive } = useBookingsBulkActions();
  const genericExportAction = useGenericExportBulkAction();

  /* PLEASE DO NOT DELETE - WE MAY NEED TO RETURN THIS METHODS
  needed for commented methods: const { openDrawer } = useDrawers();

  const produceJoin = useEvent(async (action: BulkProduceTypeEnum) => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.BULK_PRODUCE_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      Log.debug(`Bulk action one for ids`, selectedBookingIds);
      openDrawer({
        drawerProps: {
          type: DrawerType.ProductionPreview,
          action: action,
          criteria: {
            id: { $in: selectedBookingIds },
          },
        },
      });
    }
  });

  const handleProduce = useEvent(() =>
    produceJoin(BulkProduceTypeEnum.Produce),
  );
  const handleJoin = useEvent(() => produceJoin(BulkProduceTypeEnum.Join));
  const handleProduceJoin = useEvent(() =>
    produceJoin(BulkProduceTypeEnum.Both),
  );
  */

  const handleUpdateClick = useEvent(() => {
    openBulkEditForm();
  });

  const handleEditDatesAndTimesClick = useEvent(() => {
    setBulkOperationsDrawerOpened?.(false);
    openDrawer({
      drawerProps: { type: DrawerType.BulkUpdateBookingsDateTime },
    });
  });

  const handleCopyClick = useEvent(() => {
    setBulkOperationsDrawerOpened?.(false);
    setBulkCloneFormDrawerOpened(true);
  });

  const handleArchiveClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.ARCHIVE_BOOKINGS_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      bulkArchive(selectedBookingIds);
    }
  });

  const actions: IBulkActionItem[] = [
    {
      key: "update",
      label: t(`general.UPDATE`),
      hint: t(`booking.bulk.hints.update`),
      icon: PIC.PencilSVG,
      onClick: handleUpdateClick,
    },
    {
      key: "editDatesAndTimes",
      label: t(`booking.bulk.editDatesAndTimes`),
      hint: t(`booking.bulk.hints.editDatesAndTimes`),
      icon: PIC.ClockSVG,
      onClick: handleEditDatesAndTimesClick,
      permission: { entity: "booking", action: "update" },
    },
    {
      key: "copyBookings",
      label: t(`booking.bulk.copyBookings`),
      hint: t(`booking.bulk.hints.copyBookings`),
      icon: SVG_MUI.CalendarOutRight3SVG,
      onClick: handleCopyClick,
      permission: { entity: "booking", action: "create" },
    },
    genericExportAction,
    {
      key: "archive",
      label: t(`general.ARCHIVE`),
      hint: t(`booking.bulk.hints.archive`),
      icon: SVG_MUI.ArchiveSVG,
      onClick: handleArchiveClick,
    },
  ];

  return (
    <LayoutRight
      headerTitle={t(`general.BULK_ACTIONS`)}
      showFooter={false}
      closeClick={() => setBulkOperationsDrawerOpened?.(false)}
      showBackgroundImage={false}
      entity={"booking"}
    >
      <LayoutRightCrudContentStatic isFlush>
        <BulkActionsPanel actions={actions} />
      </LayoutRightCrudContentStatic>
    </LayoutRight>
  );
};
